import React from 'react';
import { Link } from 'react-router-dom';
import { useConfig } from '../../config/ConfigContext';

const Footer = () => {
  const { config } = useConfig();

  if (!config) return null;

  return (
    <footer className="relative bg-gray-100 border-t border-gray-200">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-10"
        style={{ backgroundImage: "url('/images/Bella/Product Photos/Footer.png')" }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Footer Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 mb-16">
          {/* Column 1: Brand */}
          <div className="space-y-6">
            <img
              src={config.branding.logo}
              alt={config.branding.companyName}
              className="h-12 w-auto mb-6"
            />
            <p className="text-gray-600 text-sm leading-relaxed">
              {config.branding.description}
            </p>
            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-900">Direct Contact:</p>
              <a
                href={`https://wa.me/${config.branding.orderWhatsAppNumber.replace(/[^0-9]/g, '')}`}
                className="block text-brand-blue hover:text-brand-red transition-colors text-sm font-medium"
              >
                WhatsApp: {config.branding.orderWhatsAppNumber}
              </a>
              <p className="text-gray-600 text-sm">
                Phone: {config.branding.contactPhone}
              </p>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h3 className="font-bold text-gray-900 mb-6 uppercase text-xs tracking-widest">
              Quick Links
            </h3>
            <ul className="space-y-4">
              <li>
                <Link to="/" className="text-gray-600 hover:text-brand-blue transition-colors text-sm">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/products" className="text-gray-600 hover:text-brand-blue transition-colors text-sm">
                  Full Collection
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-600 hover:text-brand-blue transition-colors text-sm">
                  Our Story
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-600 hover:text-brand-blue transition-colors text-sm">
                  Support Center
                </Link>
              </li>
              <li>
                <Link to="/cart" className="text-gray-600 hover:text-brand-blue transition-colors text-sm flex items-center">
                  My Cart
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Catalogue */}
          <div>
            <h3 className="font-bold text-gray-900 mb-6 uppercase text-xs tracking-widest">
              Our Products
            </h3>
            <ul className="space-y-4">
              <li>
                <Link to="/products?category=mattresses" className="text-gray-600 hover:text-brand-blue transition-colors text-sm">
                  Premium Mattresses
                </Link>
              </li>
              <li>
                <Link to="/products?category=pillows" className="text-gray-600 hover:text-brand-blue transition-colors text-sm">
                  Comfort Pillows
                </Link>
              </li>
              <li>
                <Link to="/products?category=sofa_beds" className="text-gray-600 hover:text-brand-blue transition-colors text-sm">
                  Sofa Beds
                </Link>
              </li>
              <li>
                <Link to="/products" className="text-gray-600 hover:text-brand-blue transition-colors text-sm">
                  New Arrivals
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-300 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">

            {/* Social Media Icons (Order 1 on Mobile, Left on Desktop) */}
            <div className="flex space-x-4 justify-center md:justify-start w-full md:w-1/3 order-1">
              {config.branding.socialLinks?.facebook && (
                <a
                  href={config.branding.socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-brand-blue rounded-full flex items-center justify-center text-white hover:bg-brand-red transition-all duration-300 hover:scale-110"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
              )}
              {config.branding.socialLinks?.instagram && (
                <a
                  href={config.branding.socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-brand-blue rounded-full flex items-center justify-center text-white hover:bg-brand-red transition-all duration-300 hover:scale-110"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.8,5.8 0 0,1 16.2,22H7.8C4.6,22 2,19.4 2,16.2V7.8A5.8,5.8 0 0,1 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M17.25,5.5A1.25,1.25 0 0,1 18.5,6.75A1.25,1.25 0 0,1 17.25,8A1.25,1.25 0 0,1 16,6.75A1.25,1.25 0 0,1 17.25,5.5Z" />
                  </svg>
                </a>
              )}
              {config.branding.socialLinks?.tiktok && (
                <a
                  href={config.branding.socialLinks.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-brand-blue rounded-full flex items-center justify-center text-white hover:bg-brand-red transition-all duration-300 hover:scale-110"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.9-.32-1.98-.23-2.81.3-.85.51-1.44 1.43-1.58 2.41-.05.35-.04.7.01 1.05.15 1.05.9 1.98 1.84 2.41.97.49 2.13.51 3.1.04.91-.42 1.57-1.3 1.76-2.28.11-.79.11-1.6-.01-2.39-.01-5.25-.01-10.51-.01-15.76z" />
                  </svg>
                </a>
              )}
            </div>

            {/* Trademark Logo (Order 2 on Mobile, Center on Desktop) */}
            <div className="flex justify-center w-full md:w-1/3 order-2">
              <a
                href="https://www.instagram.com/cyberpulse.global?igsh=MWFvcnc4MGUyMHZrbw=="
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block hover:opacity-100 opacity-80 transition-opacity"
              >
                <img
                  src="/images/Cyberpulse_transparent.png"
                  alt="CyberPulse Trademark"
                  className="h-28 w-auto object-contain max-w-full"
                />
              </a>
            </div>

            {/* Copyright (Order 3 on Mobile, Right on Desktop) */}
            <div className="text-gray-600 text-sm text-center md:text-right font-medium w-full md:w-1/3 order-3">
              © {new Date().getFullYear()} {config.branding.companyName}. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

